import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const initialForm = {
  agricultorId: '',
  transportadoraId: '',
  tomadorTipo: 'cliente',
  clienteId: '',
  fornecedorId: '',
  ambiente: 'Producao',
  serie: '1',
  numero: '',
  naturezaOperacao: 'Prestação de serviço de transporte agropecuário',
  cfop: '5353',
  cidadeOrigem: '',
  ufOrigem: '',
  cidadeDestino: '',
  ufDestino: '',
  valorServico: '',
  valorCarga: '',
  produtoPredominante: '',
  documentoReferencia: '',
  observacao: ''
};

function CteEmissao() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [agricultores, setAgricultores] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);

  const tomadores = useMemo(
    () => form.tomadorTipo === 'cliente' ? clientes : fornecedores,
    [clientes, fornecedores, form.tomadorTipo]
  );

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      try {
        const [agricultoresRes, transportadorasRes, clientesRes, fornecedoresRes, historicoRes] = await Promise.all([
          api.get('/agricultores'),
          api.get('/transportadoras'),
          api.get('/cliente'),
          api.get('/fornecedor'),
          api.get('/cte')
        ]);

        setAgricultores(Array.isArray(agricultoresRes.data) ? agricultoresRes.data : []);
        setTransportadoras(Array.isArray(transportadorasRes.data) ? transportadorasRes.data : []);
        setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
        setFornecedores(Array.isArray(fornecedoresRes.data) ? fornecedoresRes.data : []);
        setHistorico(Array.isArray(historicoRes.data) ? historicoRes.data : []);
      } catch (error) {
        console.error('Erro ao carregar dados do CT-e', error);
        showError('Não foi possível carregar os dados para CT-e.');
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, []);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const carregarHistorico = async () => {
    try {
      const { data } = await api.get('/cte');
      setHistorico(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar histórico de CT-e', error);
    }
  };

  const emitir = async (event) => {
    event.preventDefault();
    setLoading(true);
    setResultado(null);

    try {
      const payload = {
        agricultorId: Number(form.agricultorId),
        transportadoraId: form.transportadoraId ? Number(form.transportadoraId) : null,
        clienteId: form.tomadorTipo === 'cliente' && form.clienteId ? Number(form.clienteId) : null,
        fornecedorId: form.tomadorTipo === 'fornecedor' && form.fornecedorId ? Number(form.fornecedorId) : null,
        ambiente: form.ambiente,
        serie: form.serie || '1',
        numero: form.numero || null,
        naturezaOperacao: form.naturezaOperacao,
        cfop: form.cfop,
        cidadeOrigem: form.cidadeOrigem,
        ufOrigem: form.ufOrigem.toUpperCase(),
        cidadeDestino: form.cidadeDestino,
        ufDestino: form.ufDestino.toUpperCase(),
        valorServico: Number(form.valorServico || 0),
        valorCarga: Number(form.valorCarga || 0),
        produtoPredominante: form.produtoPredominante,
        documentoReferencia: form.documentoReferencia || null,
        observacao: form.observacao || null
      };

      const { data } = await api.post('/cte/emitir', payload);
      setResultado(data);
      showSuccess('CT-e enviado para a ACBr API.');
      carregarHistorico();
    } catch (error) {
      console.error('Erro ao emitir CT-e', error);
      const msg = error?.response?.data?.mensagem || error?.response?.data?.message || error?.response?.data || 'Não foi possível emitir o CT-e.';
      showError(typeof msg === 'string' ? msg : 'Não foi possível emitir o CT-e.');
      if (error?.response?.data) {
        setResultado(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const baixarArquivo = async (id, tipo, chave) => {
    try {
      const response = await api.get(`/cte/${id}/${tipo}`, { responseType: 'blob' });
      const mime = tipo === 'xml' ? 'application/xml' : 'application/pdf';
      const blob = new Blob([response.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CTe-${chave || id}.${tipo}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError(`${tipo.toUpperCase()} do CT-e não disponível.`);
    }
  };

  const historicoColumns = [
    { name: '#', selector: (row) => row.id, sortable: true, width: '70px' },
    { name: 'Emissão', selector: (row) => new Date(row.dataEmissao).toLocaleDateString(), sortable: true },
    { name: 'Status', selector: (row) => row.status || '-', sortable: true },
    { name: 'Número', selector: (row) => row.numero || '-', sortable: true },
    { name: 'Emitente', selector: (row) => row.agricultorNome || row.agricultorId || '-', sortable: true, wrap: true },
    { name: 'Tomador', selector: (row) => row.tomadorNome || '-', sortable: true, wrap: true },
    { name: 'Produto', selector: (row) => row.produtoPredominante || '-', sortable: true, wrap: true },
    { name: 'Rota', selector: (row) => `${row.cidadeOrigem}/${row.ufOrigem} -> ${row.cidadeDestino}/${row.ufDestino}`, wrap: true },
    { name: 'Servico', selector: (row) => `R$ ${Number(row.valorServico || 0).toFixed(2)}`, sortable: true, right: true },
    {
      name: 'Arquivos',
      cell: (row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-outline-secondary btn-sm" title="Baixar XML" disabled={!row.temXml} onClick={() => baixarArquivo(row.id, 'xml', row.chaveAcesso)}>
            <i className="bi bi-file-earmark-code" />
          </button>
          <button className="btn btn-outline-dark btn-sm" title="Baixar PDF" disabled={!row.temPdf} onClick={() => baixarArquivo(row.id, 'pdf', row.chaveAcesso)}>
            <i className="bi bi-filetype-pdf" />
          </button>
        </div>
      ),
      ignoreRowClick: true
    }
  ];

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-light btn-sm d-flex align-items-center gap-1" onClick={() => navigate('/menu')} disabled={loading}>
              <i className="bi bi-arrow-left text-success" />
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0 fw-semibold">CT-e / Frete Agro</h5>
              <small className="text-white-50">Frete de safra, insumos, animais, romaneio e transporte rural via ACBr API</small>
            </div>
          </div>
        </div>

        <form className="card-body" onSubmit={emitir}>
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <label className="form-label">Produtor / emitente</label>
              <select className="form-select" value={form.agricultorId} onChange={(e) => update('agricultorId', e.target.value)} required>
                <option value="">Selecione</option>
                {agricultores.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </div>
            <div className="col-12 col-lg-4">
              <label className="form-label">Transportadora</label>
              <select className="form-select" value={form.transportadoraId} onChange={(e) => update('transportadoraId', e.target.value)}>
                <option value="">Sem transportadora cadastrada</option>
                {transportadoras.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </div>
            <div className="col-12 col-lg-2">
              <label className="form-label">Ambiente</label>
              <select className="form-select" value={form.ambiente} onChange={(e) => update('ambiente', e.target.value)}>
                <option value="Producao">Produção</option>
                <option value="Homologacao">Homologação</option>
              </select>
            </div>
            <div className="col-6 col-lg-1">
              <label className="form-label">Serie</label>
              <input className="form-control" value={form.serie} onChange={(e) => update('serie', e.target.value)} required />
            </div>
            <div className="col-6 col-lg-1">
              <label className="form-label">Número</label>
              <input className="form-control" value={form.numero} onChange={(e) => update('numero', e.target.value)} />
            </div>

            <div className="col-12 col-lg-3">
              <label className="form-label">Tomador</label>
              <select className="form-select" value={form.tomadorTipo} onChange={(e) => setForm({ ...form, tomadorTipo: e.target.value, clienteId: '', fornecedorId: '' })}>
                <option value="cliente">Cliente</option>
                <option value="fornecedor">Fornecedor</option>
              </select>
            </div>
            <div className="col-12 col-lg-5">
              <label className="form-label">Cliente / fornecedor</label>
              <select
                className="form-select"
                value={form.tomadorTipo === 'cliente' ? form.clienteId : form.fornecedorId}
                onChange={(e) => update(form.tomadorTipo === 'cliente' ? 'clienteId' : 'fornecedorId', e.target.value)}
              >
                <option value="">Selecione</option>
                {tomadores.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </div>
            <div className="col-12 col-lg-2">
              <label className="form-label">CFOP</label>
              <input className="form-control" value={form.cfop} onChange={(e) => update('cfop', e.target.value)} required />
            </div>
            <div className="col-12 col-lg-2">
              <label className="form-label">Documento ref.</label>
              <input className="form-control" value={form.documentoReferencia} onChange={(e) => update('documentoReferencia', e.target.value)} />
            </div>

            <div className="col-12">
              <label className="form-label">Natureza da operação</label>
              <input className="form-control" value={form.naturezaOperacao} onChange={(e) => update('naturezaOperacao', e.target.value)} required />
            </div>

            <div className="col-12 col-md-5">
              <label className="form-label">Cidade origem</label>
              <input className="form-control" value={form.cidadeOrigem} onChange={(e) => update('cidadeOrigem', e.target.value)} required />
            </div>
            <div className="col-12 col-md-1">
              <label className="form-label">UF</label>
              <input className="form-control text-uppercase" maxLength="2" value={form.ufOrigem} onChange={(e) => update('ufOrigem', e.target.value)} required />
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label">Cidade destino</label>
              <input className="form-control" value={form.cidadeDestino} onChange={(e) => update('cidadeDestino', e.target.value)} required />
            </div>
            <div className="col-12 col-md-1">
              <label className="form-label">UF</label>
              <input className="form-control text-uppercase" maxLength="2" value={form.ufDestino} onChange={(e) => update('ufDestino', e.target.value)} required />
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Produto predominante</label>
              <input className="form-control" value={form.produtoPredominante} onChange={(e) => update('produtoPredominante', e.target.value)} placeholder="Soja, milho, bovinos, insumos..." required />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Valor do serviço</label>
              <CurrencyInput className="form-control" value={form.valorServico} onValueChange={(value) => update('valorServico', value)} placeholder="0,00" required />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Valor da carga</label>
              <CurrencyInput className="form-control" value={form.valorCarga} onValueChange={(value) => update('valorCarga', value)} placeholder="0,00" required />
            </div>

            <div className="col-12">
              <label className="form-label">Observação</label>
              <textarea className="form-control" rows="3" value={form.observacao} onChange={(e) => update('observacao', e.target.value)} />
            </div>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-success px-4" type="submit" disabled={loading}>
              <i className="bi bi-truck me-1" />
              Emitir CT-e
            </button>
          </div>
        </form>

        {resultado && (
          <div className="border-top p-3">
            <div className={`alert ${resultado.ok ? 'alert-success' : 'alert-warning'} mb-0`}>
              <div className="fw-semibold">{resultado.ok ? 'CT-e autorizado/enviado' : 'Retorno da ACBr API'}</div>
              <div>{resultado.mensagem || resultado.motivo || 'Sem mensagem de retorno.'}</div>
              {resultado.chaveAcesso && <div className="small mt-2">Chave: {resultado.chaveAcesso}</div>}
              {resultado.protocolo && <div className="small">Protocolo: {resultado.protocolo}</div>}
            </div>
          </div>
        )}
      </div>

      <div className="card shadow-lg border-0 rounded-3 mt-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold">Histórico de CT-e</h6>
          <button className="btn btn-outline-success btn-sm" onClick={carregarHistorico} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Atualizar
          </button>
        </div>
        <div className="card-body">
          <DataTable
            columns={historicoColumns}
            data={historico}
            pagination
            responsive
            striped
            highlightOnHover
            progressPending={loading}
            noDataComponent="Nenhum CT-e emitido."
          />
        </div>
      </div>
    </div>
  );
}

export default CteEmissao;
